import { CircularProgress } from "@mui/material";
import styled from "styled-components";
import { banks_logos } from "@/assets/images/banks";
import { connectPayPalIntegration } from "../../actions/payment-methods/actions";
import { PaymentCardIcon, PaymentMethodButton } from ".";
import { CardTitle, CardDescription } from "../../pages/payment-methods/styled";
import { capitalizeFirstLetter } from "@/utils/string-manipulation";
import { useState, Fragment, useCallback, useEffect } from "react";
import { Plaid } from "@/components/payment-methods";
import { Edit, Info, Loader2 } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button-tw";
import { toast } from "react-toastify";
import { useSetDefaultPayment } from "@/hooks/payment/use-make-primary";
import { useAuth } from "@/providers/auth";
import { cn } from "@/utils/classnames";
import { usePlaidLink } from "react-plaid-link";
import api from "@/api/v1-jwt/config/api";
import { useQueryClient } from "@tanstack/react-query";
import setAccessToken from "@/pages/plaid/actions/setAccesToken";
import useUpdatePaymentMethod from "@/queries/payment-method/use-update-payment-method";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { z } from "zod";
import paymentMethodQueryKeys from "@/queries/payment-method/query-keys";

const PaymentCard = ({
  paymentMethod,
  loading,
  settingData,
  className,
  method_type,
  payment_method_status,
  payment_method_name,
  primary,
  bank_name,
  account_type,
  handleDisconnect,
  payment_method_id,
  last_4,
  access_token = "",
  account,
  name_on_account,
  paymentMethodDefault,
  accountRouting,
  setPaymentList,
}) => {
  const { mutate: makePrimary } = useSetDefaultPayment();
  const { cognitoId: userId } = useAuth();
  const [addNicknameModalOpen, setAddNicknameModalOpen] = useState(false);
  const [plaidOpen, setPlaidOpen] = useState(false);

  const togglePlaid = () => {
    setPlaidOpen((prev) => !prev);
  };

  const handleConnectPaypal = async () => {
    const clientCognitoId = JSON.parse(localStorage.getItem("PLATFORM_ID"));
    if (!clientCognitoId) {
      throw new Error("It was not possible to identify the platform.");
    }

    await connectPayPalIntegration({
      cognito_id: userId,
    }).then((res) => {
      if (res && res.redirect_uri) {
        window.open(res.redirect_uri);
      }
    });
  };

  const handleMakePrimary = () => {
    makePrimary(payment_method_id, {
      onSuccess: () => {
        setPaymentList((prevList) => {
          const newList = prevList.map((item) => {
            if (item.payment_method_default) {
              return { ...item, payment_method_default: false };
            }

            if (item.payment_method_id === payment_method_id) {
              return { ...item, payment_method_default: true };
            }

            return item;
          });

          return newList;
        });

        toast.success("Your primary account has been updated");
      },
      onError: (error) => {
        console.log("ERROR::", error);
        toast.error(
          "There was an error setting default payment method. Please try again."
        );
      },
    });
  };
  // const { linkToken, loadingToken, error, openPlaidLink, readyPlaidLink } =
  //   usePlaidLinkWithStatus(payment_method_status, method_type, access_token);
  if (loading) {
    return (
      <div className={className}>
        <div style={{ margin: "auto" }}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (method_type === "PAYPAL") {
    return null;
    // <div
    //   className={cn("flex flex-col justify-between p-6 md:p-8", className)}
    // >
    //   <div>
    //     {/* <StyledCircle /> */}
    //     <PaymentCardIcon bank_name={bank_name} method_type={method_type} />
    //     <CardTitle>{payment_method_name}</CardTitle>
    //     <CardDescription>
    //       {payment_method_status === "active" ? "" : ""}
    //     </CardDescription>
    //   </div>
    //   <div>
    //     <PaymentMethodButton
    //       primary_color={settingData?.primary_color}
    //       status={payment_method_status}
    //       handleDisconnect={() => handleDisconnect()}
    //       handleConnect={() => handleConnectPaypal()}
    //     />
    //   </div>
    // </div>
  }
  if (method_type === "PLAID") {
    return (
      <div
        className={cn("flex flex-col justify-between p-6 md:p-8", className)}
      >
        <div>
          <div className="flex justify-between items-start">
            <PaymentCardIcon bank_name={bank_name} method_type={method_type} />
            {primary && (
              <div className="ml-2 inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                <svg
                  className="h-1.5 w-1.5 fill-green-500"
                  viewBox="0 0 6 6"
                  aria-hidden="true"
                >
                  <circle cx={3} cy={3} r={3} />
                </svg>
                Primary
                <HoverCard>
                  <HoverCardTrigger>
                    <Info className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                  </HoverCardTrigger>
                  <HoverCardContent>
                    Your default account for payments and transfers.
                  </HoverCardContent>
                </HoverCard>
              </div>
            )}
          </div>
          <div className="flex items-center gap-x-3 mt-5 mb-2">
            <h3 className="truncate text-xl font-medium text-gray-900">
              {`${
                !banks_logos[bank_name] ? bank_name || "Unrecognized" : ""
              } ${capitalizeFirstLetter(account_type || "")}`}
            </h3>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setAddNicknameModalOpen(true)}
            >
              <Edit className="h-4 w-4 " />
            </Button>
          </div>
          <CardDescription>{`****${last_4}`}</CardDescription>
        </div>
        <div>
          <div className="flex gap-x-2 justify-between">
            {payment_method_status === "pending_automatic_verification" ? (
              <div className="w-full rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                Your account is being verified automatically. This may take 1–2 business days.
              </div>
            ) : payment_method_status === "pending_manual_verification" ? (
              <Button
                className="w-full bg-[#FFA500] hover:bg-[#e09400] text-white"
                onClick={togglePlaid}
              >
                Verify
              </Button>
            ) : payment_method_status === "pending_disconnect" ? (
              <UpdatePaymentMethod
                paymentMethodId={payment_method_id}
                name={name_on_account}
                setPaymentList={setPaymentList}
                paymentMethodDefault={paymentMethodDefault}
                className="w-full"
              />
            ) : (
              <>
                <PaymentMethodButton
                  primary_color={settingData?.primary_color}
                  status={payment_method_status}
                  handleDisconnect={() =>
                    handleDisconnect(
                      method_type,
                      payment_method_id,
                      `${bank_name} ending in ${last_4}`
                    )
                  }
                  handleConnect={togglePlaid}
                />
                {!primary && payment_method_status === "active" && (
                  <Button variant="outline" onClick={handleMakePrimary}>
                    Make primary
                  </Button>
                )}
              </>
            )}
          </div>
          <AddNicknameModal
            open={addNicknameModalOpen}
            onOpenChange={setAddNicknameModalOpen}
            paymentMethodId={payment_method_id}
            userId={userId}
          />
          {plaidOpen && (
            <Plaid
              open={plaidOpen}
              onClose={togglePlaid}
              method_type={method_type}
              payment_method_status={payment_method_status}
              access_token={access_token}
              setPaymentList={setPaymentList}
              key={payment_method_id}
              name_on_account={name_on_account}
              linkData={{
                firstName: paymentMethod?.account_first_name,
                lastName: paymentMethod?.account_last_name,
                businessName: paymentMethod?.account_business_name,
                accountType: paymentMethod?.account_type,
              }}
            />
          )}
        </div>
      </div>
    );
  }
  return (
    <div className={cn("flex flex-col justify-between p-6 md:p-8", className)}>
      <div>
        <PaymentCardIcon />
        <CardTitle>Add new bank account</CardTitle>
      </div>
      <div>
        <PaymentMethodButton
          handleConnect={togglePlaid}
          primary_color={settingData?.primary_color}
        />
        {plaidOpen ? (
          <Plaid
            open={plaidOpen}
            onClose={togglePlaid}
            method_type={"PLAID"}
            access_token=""
            accountType={accountRouting ? "routing_account" : "default"}
            key={payment_method_id}
          />
        ) : null}
      </div>
    </div>
  );
};

const NicknameFormSchema = z.object({
  nickname: z.string().min(1, "Nickname is required"),
});

function AddNicknameModal({ open, onOpenChange, userId, paymentMethodId }) {
  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(NicknameFormSchema),
  });
  const updatePaymentMethod = useUpdatePaymentMethod();
  const saveDisabled = !isValid || updatePaymentMethod.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form
          onSubmit={handleSubmit(async (values) => {
            const payload = {
              payment_method_id: paymentMethodId,
              user_id: userId,
              payment_method_nickname: values.nickname,
            };

            updatePaymentMethod.mutate(payload, {
              onSuccess: () => {
                reset();
                onOpenChange(false);
              },
            });
          })}
        >
          <DialogHeader>
            <DialogTitle className="text-xl">
              <div>Add Account Nickname</div>
            </DialogTitle>
            <DialogDescription>
              Depending on the method used to link your account the institution
              information might be missing. Add a nickname so you can better
              identify the linked account.
            </DialogDescription>
          </DialogHeader>

          <div className="pt-4">
            <Label htmlFor="nickname">Account Nickname</Label>
            <Input {...register("nickname")} />
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              className="w-1/2"
              type="button"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button className="w-1/2" type="submit" disabled={saveDisabled}>
              {updatePaymentMethod.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function UpdatePaymentMethod({
  paymentMethodId,
  name,
  paymentMethodDefault,
  className,
}) {
  const { cognitoId } = useAuth();
  const queryClient = useQueryClient();
  const [linkToken, setLinkToken] = useState(null);

  useEffect(() => {
    const updateLinkToken = async () => {
      try {
        const { data } = await api.patch(
          "/platform/payment_methods/plaid/link-token/update",
          {
            user_id: cognitoId,
            payment_method_id: paymentMethodId,
          }
        );
        setLinkToken(data?.data?.link_token);
      } catch (error) {
        console.log(error);
      }
    };
    updateLinkToken();
  }, []);

  const onSuccess = useCallback(async (publicToken, metadata) => {
    // https://plaid.com/docs/api/tokens/#token-exchange-flow
    try {
      const newPaymentMethod = await setAccessToken(
        publicToken,
        metadata,
        cognitoId,
        name,
        paymentMethodDefault
      );

      toast.success(`Your bank account has been updated.`);
      queryClient.invalidateQueries(paymentMethodQueryKeys.all);
    } catch (error) {
      toast.error("There was an error updating the account. Please try again.");
    }
  }, []);

  const onExit = useCallback((error) => {
    if (error != null && error.error_code === "INVALID_LINK_TOKEN") {
      toast.error(
        "Something went wrong linking your account. Please try connecting again."
      );
    }

    if (error != null) {
      toast.error(error.display_message);

      console.log(
        `USER ${cognitoId} EXITED PLAID WITH ERROR TYPE ${error.error_type} :: CODE ${error.error_code}`
      );
    }
  }, []);

  const config = {
    token: linkToken,
    onSuccess,
    onExit,
    // onEvent,
  };

  const { open, ready } = usePlaidLink(config);

  return (
    <Button
      onClick={() => open()}
      disabled={!ready}
      className={cn("w-1/2 bg-yellow-500 hover:bg-yellow-600", className)}
    >
      Verify
    </Button>
  );
}

const PaymentCardStyled = styled(PaymentCard)`
  background: #ffffff;
  box-shadow: 0px 1px 3px rgba(16, 24, 40, 0.1),
    0px 1px 2px rgba(16, 24, 40, 0.06);
  border-radius: 12px;
  width: 100%;
  max-width: 362px;
  height: 285px;
  display: flex;

  border: 3px solid transparent; /* default border style */
  overflow: hidden;

  ${(props) =>
    props.payment_method_status === "active" && !props.loading
      ? "border-color: #1DB45A"
      : null}

  ${(props) =>
    [
      "pending_manual_verification",
      "pending_automatic_verification",
      "pending",
      "database_insights_pending",
    ].includes(props.payment_method_status) && !props.loading
      ? "border-color: #FFA500"
      : null}
`;

export default PaymentCardStyled;
